import { useCallback } from 'react';
import { Box, Modal, ModalContent, ModalOverlay, ModalFooter } from '@chakra-ui/react';
import { Button, Text } from '@mercator-gcp/grout';
import { useDeleteGroup } from 'hooks/useDeleteGroup';
import { useInvalidateGroupConfigurations } from 'hooks/useGroupConfig';
import { useUser } from 'state/auth';
import { useDashboardGroupType } from 'state/dashboard';

const contentStyle = {
  maxWidth: '500px',
  width: 'fit-content',
};

const titleStyle = {
  paddingBottom: 'padding_16',
};

const modalBodyStyle = {
  padding: 'padding_16',
  paddingBottom: '0px',
};

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: 'padding_16',
};

type DeleteProjectGroupModalProps = {
  isOpen: boolean;
  onDeleteCallback?: () => void;
  onClose: () => void;
  groupId: string | number;
};

const DeleteProjectGroupModal = ({
  isOpen,
  onClose,
  onDeleteCallback,
  groupId,
}: DeleteProjectGroupModalProps) => {
  const { deleteGroup } = useDeleteGroup();
  const { invalidateGroupConfigurations } = useInvalidateGroupConfigurations();
  const { user } = useUser();
  const [type] = useDashboardGroupType();

  const handleDeleteSuccess = useCallback(() => {
    invalidateGroupConfigurations(String(user?.id), type);
    if (onDeleteCallback) {
      onDeleteCallback();
    }
  }, [onDeleteCallback, invalidateGroupConfigurations]);

  const onDeleteClick = useCallback(() => {
    if (deleteGroup.isLoading) {
      return;
    }
    deleteGroup.mutate({
      groupId: groupId.toString(),
      onSuccessCallback: handleDeleteSuccess,
    });
  }, [deleteGroup, groupId, handleDeleteSuccess]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent sx={contentStyle}>
        <Box sx={modalBodyStyle}>
          <Text.Lead sx={titleStyle}>
            Are you sure you want to delete this group?
          </Text.Lead>
          <Text>This action cannot be undone.</Text>
        </Box>
        <ModalFooter sx={modalFooterStyle}>
          <Button
            variant="functional"
            onClick={onClose}
            isDisabled={deleteGroup.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="alert"
            onClick={onDeleteClick}
            isLoading={deleteGroup.isLoading}
          >
            Yes, delete this group
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteProjectGroupModal;
